import { TRASH_RETENTION_DAYS, dayDifferenceInZone } from '@cleartask/shared';
import { useCurrentUser } from '../../session.js';
import { useRestoreTask, useTaskList } from '../tasks/useTasks.js';

/**
 * Lixeira (spec 3 e 15, fase 9).
 *
 * O modelo existe desde a fase 2 — `deleted_at` mais a `view=trash` da API —,
 * mas nao havia tela: uma tarefa excluida sumia e so voltava por "Desfazer",
 * dentro dos 8 segundos do toast. Passado isso, ela existia no banco e era
 * inalcancavel.
 *
 * Nao ha botao de excluir definitivamente, e isso e deliberado: a API nao expoe
 * essa operacao. Quem apaga de verdade e o cron das 06:00, DEPOIS de gravar o
 * backup do dia no R2. Um botao aqui destruiria a linha antes do proximo
 * backup — a unica acao do app sem volta possivel, e sem rede de seguranca
 * atras dela.
 */
export function TrashList() {
  const user = useCurrentUser();

  const { data, isPending } = useTaskList({ view: 'trash', includeDone: true });

  // A mutacao saiu daqui para `useTasks.ts` em 12/08/2026: enquanto morava
  // nesta tela, a lixeira era o unico lugar do app capaz de restaurar — o
  // painel de uma tarefa excluida aberta por link so sabia mandar a pessoa vir
  // ate aqui procurar a mesma tarefa de novo.
  const restaurar = useRestoreTask();

  if (isPending) {
    return <p className="p-4 text-sm text-text-mut">Carregando lixeira…</p>;
  }

  const tarefas = data?.tasks ?? [];
  const agora = Date.now();

  if (tarefas.length === 0) {
    return (
      <p className="rounded bg-surface p-4 text-center text-sm text-text-mut shadow-card">
        A lixeira está vazia. O que você excluir aparece aqui por {TRASH_RETENTION_DAYS} dias.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-text-mut">
        Excluídas ficam aqui por {TRASH_RETENTION_DAYS} dias e são apagadas em definitivo no
        backup diário.
      </p>

      <ul className="flex flex-col gap-2">
        {tarefas.map((task) => {
          // Dias RESTANTES, nao decorridos: quem abre a lixeira quer saber
          // quanto tempo ainda tem para mudar de ideia.
          const decorridos =
            task.deletedAt === null
              ? 0
              : dayDifferenceInZone(user.timezone, task.deletedAt, agora);
          const restantes = Math.max(0, TRASH_RETENTION_DAYS - decorridos);

          return (
            <li
              key={task.id}
              className="flex items-center gap-3 rounded bg-surface p-3 shadow-card"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate">{task.title}</p>
                <p className="text-sm text-text-mut">
                  {restantes === 0
                    ? 'será apagada no próximo backup'
                    : `${restantes} ${restantes === 1 ? 'dia restante' : 'dias restantes'}`}
                </p>
              </div>

              <button
                type="button"
                onClick={() => restaurar.mutate(task)}
                disabled={restaurar.isPending}
                className="min-h-touch shrink-0 rounded-sm bg-surface-2 px-3 text-sm font-medium"
              >
                Restaurar
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
